import type { TFunction } from "i18next";
import { useRef, type RefObject } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputDecimal from "../../../../../u2000Core/components/formControls/input/U2000InputDecimal";
import U2000InputCurrency from "../../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000Label from "../../../../../u2000Core/components/formControls/label/U2000Label";
import U2000CheckBox from "../../../../../u2000Core/components/formControls/checkbox/U2000CheckBox";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U3125_INVOICE_LINE_KINDS, type U3125InvoiceLineGridRow } from "../../../../U3125Service";
import type { U3125FactureNestedParentHandlers, U3125InvoiceLineHandlerReturn } from "./U3125InvoiceLineHandlers";

interface U3125InvoiceLineEditDlgProps {
  u3125InvoiceLineHandler: U3125InvoiceLineHandlerReturn;
  parentFormHandlers: U3125FactureNestedParentHandlers;
  t: TFunction;
  NS: string;
}

function U3125InvoiceLineEditDlg({ u3125InvoiceLineHandler, parentFormHandlers, t, NS }: U3125InvoiceLineEditDlgProps) {
  const { formStateHandlers, onDlgClose, isDialogOpen } = u3125InvoiceLineHandler;
  const descriptionRef = useRef<HTMLInputElement>(null);
  const lineData = formStateHandlers.getData();

  const handleClose = (result: string) => {
    if (result === "cancel") {
      onDlgClose("cancel");
    } else {
      onDlgClose("save");
    }
  };

  const parentReadOnly = parentFormHandlers.getFormState?.() === U2000FormStateEnumState.READ;
  const nestedNone = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.NONE;
  const readOnly = parentReadOnly || nestedNone;

  return (
    <U2000SaveCancelDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      title={t("sectionInvoiceLines", { ns: NS })}
      isValid={true}
      readOnly={readOnly}
      defaultFocusRef={descriptionRef as RefObject<HTMLElement>}
    >
      <div className="flex flex-col gap-y-4">
        <div className="space-y-1.5">
          <U2000Label label={t("lblLineKind", { ns: NS })} />
          <select
            className="w-full rounded-lg border border-gray-200 bg-surface px-4 py-3 text-sm"
            disabled={readOnly}
            value={lineData?.kind ?? "LABOR"}
            onChange={(e) => {
              const d = formStateHandlers.getData();
              if (!d) return;
              formStateHandlers.setData({ ...d, kind: e.target.value as U3125InvoiceLineGridRow["kind"] });
            }}
          >
            {U3125_INVOICE_LINE_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <U2000InputText
          fieldName="description"
          label={t("lblLineDescription", { ns: NS })}
          formStateHandlers={formStateHandlers}
          inputRef={descriptionRef as React.RefObject<HTMLInputElement>}
          required={true}
          readonly={readOnly}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <U2000InputDecimal fieldName="quantity" label={t("lblLineQuantity", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
          <U2000InputCurrency fieldName="unitPrice" label={t("lblLineUnitPrice", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} decimalDigits={2} allowNegative />
          <U2000InputCurrency fieldName="amount" label={t("lblLineAmount", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} decimalDigits={2} allowNegative />
        </div>
        <U2000CheckBox fieldName="taxable" label={t("lblLineTaxable", { ns: NS })} formStateHandlers={formStateHandlers} readonly={readOnly} />
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3125InvoiceLineEditDlg;
